import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DoubleRingImg from "../../assets/images/DoubleRing.png"
import StepTwo from "./steps/StepTwo";
import axiosInstance from "../../api/axiosInstance";
import InactivityTimer from "../../components/InactivityTimer";

const PatientMakePaymentPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [appointmentData, setAppointmentData] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        // Get appointment data from location state or localStorage
        const data = location.state?.appointmentData || JSON.parse(localStorage.getItem('appointmentData') || 'null');
        if (data) {
            setAppointmentData(data);
        } else {
            // If no appointment data, redirect back
            navigate('/book-appointment');
        }
    }, [location, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("submit");
    }

    const handlePayment = async () => {
        if (!appointmentData) return;

        setIsProcessing(true);
        
        try {
            console.log('Creating consultation order with data:', {
                amount: appointmentData.consultationFee,
                appointmentData: appointmentData
            });

            // Create Razorpay order for consultation fee
            // Token is automatically sent via axiosInstance interceptor
            const orderResponse = await axiosInstance.post('/api/patient/create-consultation-order', {
                amount: appointmentData.consultationFee,
                appointmentData: appointmentData,
                siteCode: localStorage.getItem('site_code') || null,
                himsToken: localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1') || null, // HIMS token for server-side fee verification
            });

            const { razorpayOrderId, amount, currency, key } = orderResponse.data;

            // Create form and submit to Razorpay (same as registration flow)
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://api.razorpay.com/v1/checkout/embedded';

            const formFields = {
                key_id: key,
                amount: amount.toString(),
                currency: currency,
                name: "KIMS Hospital",
                description: `Consultation Fee - ${appointmentData.doctorName}`,
                image: "/logo.jpg",
                order_id: razorpayOrderId,
                callback_url: `${axiosInstance.defaults.baseURL}/api/patient/verify-payment`,
                cancel_url: `${axiosInstance.defaults.baseURL}/api/patient/cancel-payment`,
                notes: {
                    appointment_data: JSON.stringify(appointmentData),
                    payment_type: 'consultation'
                },
                prefill: {
                    name: appointmentData.patientName || '',
                    email: appointmentData.patientEmail || '',
                    contact: appointmentData.patientMobile || '',
                },
                theme: { color: "#0FA968" },
            };

            // Add all form fields
            Object.keys(formFields).forEach((key) => {
                if (typeof formFields[key] === 'object') {
                    Object.keys(formFields[key]).forEach((subKey) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = `${key}[${subKey}]`;
                        input.value = formFields[key][subKey];
                        form.appendChild(input);
                    });
                } else {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = formFields[key];
                    form.appendChild(input);
                }
            });

            document.body.appendChild(form);
            form.submit();
        } catch (error) {
            console.error('Error creating order:', error);
            alert('Failed to initiate payment. Please try again.');
            setIsProcessing(false);
        }
    };

    return(
        <>
            <InactivityTimer />
            <div className="page-container flex flex-col">
                <div className="form-container flex-grow overflow-auto">
                    <StepTwo />
                <form onSubmit={handleSubmit}>
                    <>
                        {/* Appointment Details */}
                        {appointmentData && (
                            <div className="flex justify-center px-4">
                                <div className="mt-6 bg-[#F5F5F5] rounded-lg p-4 sm:p-6 w-full max-w-[500px]">
                                    <p className="font-robot text-[#0FA968] mb-4" style={{fontWeight: "600", fontSize: "20px"}}>Appointment Details</p>
                                    
                                    <div className="space-y-3 text-[#27272A]">
                                        <div className='flex justify-between'>
                                            <span className='text-[14px]' style={{fontWeight: "500"}}>Department:</span>
                                            <span className='text-[14px]' style={{fontWeight: "600"}}>{appointmentData.departmentName}</span>
                                        </div>
                                        <div className='flex justify-between'>
                                            <span className='text-[14px]' style={{fontWeight: "500"}}>Doctor:</span>
                                            <span className='text-[14px]' style={{fontWeight: "600"}}>{appointmentData.doctorName}</span>
                                        </div>
                                        <div className='flex justify-between'>
                                            <span className='text-[14px]' style={{fontWeight: "500"}}>Service:</span>
                                            <span className='text-[14px]' style={{fontWeight: "600"}}>{appointmentData.serviceName}</span>
                                        </div>
                                        <div className='flex justify-between'>
                                            <span className='text-[14px]' style={{fontWeight: "500"}}>Date:</span>
                                            <span className='text-[14px]' style={{fontWeight: "600"}}>{appointmentData.appointmentDate}</span>
                                        </div>
                                        <div className='flex justify-between'>
                                            <span className='text-[14px]' style={{fontWeight: "500"}}>Time Slot:</span>
                                            <span className='text-[14px]' style={{fontWeight: "600"}}>{appointmentData.appointmentSlot}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bill Details */}
                        {appointmentData && (
                            <div className="flex justify-center px-4">
                                <div className="mt-6 bg-[#D9D9DE] rounded-lg p-4 sm:p-6 w-full max-w-[500px]">
                                    <p className="font-robot text-[#0FA968]" style={{fontWeight: "500", fontSize: "18px"}}>Bill Details</p>

                                    <div className="mt-2 text-[#27272A]">
                                        <div className='grid grid-cols-[2fr_2fr]'>
                                            <div>
                                                <h3 className='text-[14px]' style={{fontWeight: "500"}}>Consultation Charge:</h3>
                                            </div>
                                            <div>
                                                <span className='text-[14px] float-right' style={{fontWeight: "500"}}>₹{appointmentData.consultationFee?.toFixed(2)}</span>
                                            </div>
                                        </div>
                                        <div className='grid grid-cols-[2fr_2fr]'>
                                            <div>
                                                <h3 className='text-[14px]' style={{fontWeight: "500"}}>Service Tax:</h3>
                                            </div>
                                            <div>
                                                <span className='text-[14px] float-right' style={{fontWeight: "500"}}>₹0.00</span>
                                            </div>
                                        </div>
                                        <div className="mt-2 mb-2 border-b-2 border-[#92929E]"></div>
                                        <div className='grid grid-cols-[2fr_2fr]'>
                                            <div>
                                                <h3 className='text-[16px]' style={{fontWeight: "700"}}>Total Payable Amount:</h3>
                                            </div>
                                            <div>
                                                <span className='text-[16px] float-right' style={{fontWeight: "700"}}>₹{appointmentData.consultationFee?.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Payment Button */}
                        <div className="flex flex-col-reverse sm:flex-row justify-center items-stretch sm:items-center mt-8 mb-6 gap-3 sm:gap-4 px-4">
                            <button
                                type="button"
                                onClick={() => navigate('/book-appointment')}
                                className="px-6 py-3 bg-[#EFFEF7] text-[#0FA968] font-semibold rounded-lg shadow hover:bg-gray-200 transition duration-200 border border-[#0FA968]"
                                disabled={isProcessing}
                            >
                                Go Back
                            </button>
                            <button
                                type="button"
                                onClick={handlePayment}
                                className="px-8 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200 text-[18px]"
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : `Pay ₹${appointmentData?.consultationFee?.toFixed(2)} Now`}
                            </button>
                        </div>

                        {isProcessing && (
                            <>
                                {/* Payment processing section */}
                                <div className="flex justify-center mt-4 mb-6">
                                    <img src={DoubleRingImg} className="h-[110px] w-[110px]" alt="Processing" />
                                </div>
                                <div className="flex justify-center mt-4 mb-6">
                                    <p className="text-[#565661] text-[16px]" style={{fontWeight: "600"}}>Hold on. We are processing your payment.</p>
                                </div>
                                <div className="flex justify-center mt-4 mb-6">
                                    <p className="text-[#E91245] text-[16px]" style={{fontWeight: "600"}}>Don't go back to other pages</p>
                                </div>
                            </>
                        )}
                    </>
                </form>
            </div>
        </div>
        </>
    );
}

export default PatientMakePaymentPage;
